import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Wrench, FileText, Calculator, Settings2, Sparkles, Clock, ArrowRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import WorkspaceLoader from './WorkspaceLoader';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const IndependentUtilitiesView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <WorkspaceLoader />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  interface UtilityItem {
    id: string;
    title: string;
    description: string;
    icon: any;
    bg: string;
    border: string;
    iconBg: string;
    iconColor: string;
    hoverBg: string;
    status: 'Ready' | 'Coming Soon' | 'Upcoming';
    route?: string;
  }

  const utilities: UtilityItem[] = [
    {
      id: 'pdf-controls',
      title: 'PDF Controls',
      description: 'Customize headers, branding, and export settings for your documents.',
      icon: Settings2,
      bg: 'bg-indigo-50/50 dark:bg-indigo-950/20',
      border: 'border-indigo-100 dark:border-indigo-900/30',
      iconBg: 'bg-indigo-100 dark:bg-indigo-900/40',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      hoverBg: 'hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white',
      status: 'Ready',
      route: '/dashboard/utilities/pdf-converter'
    },
    {
      id: 'quick-search',
      title: 'Quick Search',
      description: 'Instantly find lesson plans, students, and resources across your workspace.',
      icon: Search,
      bg: 'bg-blue-50/50 dark:bg-blue-950/20',
      border: 'border-blue-100 dark:border-blue-900/30',
      iconBg: 'bg-blue-100 dark:bg-blue-900/40',
      iconColor: 'text-blue-600 dark:text-blue-400',
      hoverBg: 'hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 dark:hover:text-white',
      status: 'Ready',
      route: '/dashboard/utilities/search'
    },
    {
      id: 'calculator',
      title: 'Math Toolkit',
      description: 'Quick calculator and grading tools for simplified marking.',
      icon: Calculator,
      bg: 'bg-emerald-50/50 dark:bg-emerald-950/20',
      border: 'border-emerald-100 dark:border-emerald-900/30',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/40',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      hoverBg: 'hover:bg-emerald-600 hover:text-white dark:hover:bg-emerald-600 dark:hover:text-white',
      status: 'Ready',
      route: '/dashboard/utilities/calculator'
    },
    {
      id: 'ai-enhancer',
      title: 'Content Enhancer',
      description: 'Use AI to refine and polish your typed lesson content.',
      icon: Sparkles,
      bg: 'bg-pink-50/50 dark:bg-pink-950/20',
      border: 'border-pink-100 dark:border-pink-900/30',
      iconBg: 'bg-pink-100 dark:bg-pink-900/40',
      iconColor: 'text-pink-600 dark:text-pink-400',
      hoverBg: '',
      status: 'Coming Soon'
    },
    {
      id: 'timer',
      title: 'Classroom Timer',
      description: 'Visual countdown and stopwatch for timed activities.',
      icon: Clock,
      bg: 'bg-amber-50/50 dark:bg-amber-950/20',
      border: 'border-amber-100 dark:border-amber-900/30',
      iconBg: 'bg-amber-100 dark:bg-amber-900/40',
      iconColor: 'text-amber-600 dark:text-amber-400',
      hoverBg: '',
      status: 'Upcoming'
    }
  ];

  return (
    <motion.div 
      className="space-y-8 animate-in fade-in duration-500"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      <div className="border-b border-gray-100 dark:border-white/5 pb-6">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 flex items-center gap-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Wrench className="w-6 h-6 text-blue-600" />
          </div>
          Workspace Utilities
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          A collection of specialized tools designed to streamline your teaching workflow and enhance your resource management.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {utilities.map((util) => (
          <motion.div key={util.id} variants={itemVariants}>
            <Card className={`${util.bg} ${util.border} border shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col backdrop-blur-sm rounded-xl`}>
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div className="space-y-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className={`text-[10px] font-bold uppercase tracking-widest cursor-help ${util.status === 'Ready' ? util.iconColor : 'text-slate-400 dark:text-slate-500'}`}>
                        {util.status}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-[10px]">{util.status === 'Ready' ? 'This utility is fully functional and ready to use.' : util.status === 'Coming Soon' ? 'This utility is currently under development and will be available soon.' : 'This utility is planned for a future release.'}</p>
                    </TooltipContent>
                  </Tooltip>
                  <CardTitle className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{util.title}</CardTitle>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`p-2.5 rounded-lg ${util.iconBg} group-hover:scale-105 transition-transform duration-300 cursor-help`}>
                      <util.icon className={`w-5 h-5 ${util.iconColor}`} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-[10px]">{util.description}</p>
                  </TooltipContent>
                </Tooltip>
              </CardHeader>
              <CardContent className="flex-1 pt-2">
                <CardDescription className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
                  {util.description}
                </CardDescription>
              </CardContent>
              <div className="p-6 pt-0 mt-auto">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      disabled={util.status !== 'Ready'}
                      onClick={() => util.route && navigate(util.route)}
                      className={`w-full group/btn ${util.status === 'Ready' ? 'hover:bg-blue-600 hover:text-white dark:hover:bg-blue-500 dark:hover:text-white border-gray-200 dark:border-white/10' : 'opacity-50'}`}
                    >
                      <span className="text-xs font-bold uppercase tracking-widest mr-2">
                        {util.status === 'Ready' ? 'Launch Utility' : 'Coming Soon'}
                      </span>
                      {util.status === 'Ready' && <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-[10px]">{util.status === 'Ready' ? `Open ${util.title} and start working` : 'This utility is not yet available'}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="p-8 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-1">
          <h3 className="font-bold text-lg">Have a tool in mind?</h3>
          <p className="text-sm text-muted-foreground">We're constantly adding new specialized tools for independent educators.</p>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="link" className="text-blue-600 dark:text-blue-400 font-bold tracking-tight p-0 h-auto">
              Request a Custom Utility <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-[10px]">Suggest a new utility or feature you'd like to see in the workspace.</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </motion.div>
  );
};

export default IndependentUtilitiesView;
